import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { IBrandSafetyRule } from '~/src/dash/api/brand-safety-rules';
import {
  batchUpdateCreativeValidationStatus,
  getCreativeValidationsV2,
  ICreativeValidation,
  REVIEW_STATUS_ALLOWED,
  REVIEW_STATUS_BLOCKED,
  REVIEW_STATUS_BLOCKED_BY_API,
  REVIEW_STATUS_REVIEWED,
  REVIEW_STATUS_UNREVIEWED,
} from '~/src/dash/api/creative-validation';
import {
  addDomainAndTokenToUrl,
  addQueryParams,
  createUrl,
  IHttpResponse,
  restGet,
} from '~/src/dash/api/rest';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ICreativeValidationRowData } from '~/src/dash/components/list-table';
import EditCreativeReviewModal from '~/src/dash/components/modal-dialog-edit-creative-review';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import { IconTextToggle } from '~/src/dash/components/toggles/IconTextToggle';
import {
  Col,
  Cols,
  fontSizing,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  RouteBidderSettingsExisting,
} from '~/src/dash/router/routes';
import { graphColors, muiColors, publicaColors } from '~/src/dash/utils/colors';
import { IABCategoryCodesToLabels } from '~/src/dash/utils/iab-category';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { BatchUpdateModal } from '~/src/dash/views/brand-safety/creative-review/batch-update-modal';
import {
  createAction,
  formatCreativeReviewResponseIntoLVTRows,
  ICustomCreativeReviewAPIFilterConfig,
} from '~/src/dash/views/brand-safety/creative-review/config';
import { CreativeReviewFilters } from '~/src/dash/views/brand-safety/creative-review/CreativeReviewFilters';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const transformCreativeValidationToRowData = (
  cv: ICreativeValidation,
  idx: number,
): ICreativeValidationRowData => {
  return {
    ID: idx.toString(),
    PublisherID: cv.PublisherID,
    Adomain: cv.Adomain,
    AdomainEnrichedBy: cv.AdomainEnrichedBy,
    AdomainOverride: cv.AdomainOverride,
    IABCategory: IABCategoryCodesToLabels(cv.IABCategory, ';'),
    IABCategoryCodes: cv.IABCategory,
    IABCategoryEnrichedBy: cv.CategoryEnrichedBy,
    IABCategoryOverride: cv.IABCategoryOverride,
    OriginalCreativeURL: cv.OriginalCreativeURL,
    CreativeURL: cv.CreativeURL,
    Impressions: cv.Impressions,
    BidErrors: cv.BidErrors,
    BidErrorBlockedResponses: cv.BidErrorBlockedResponses,
    BidErrorsBrandSafety: cv.BidErrorsBrandSafety,
    BidderID: cv.BidderID,
    BidderName: cv.BidderName,
    ReviewStatus: cv.ReviewStatus,
    Notes: cv.Notes,
    BrandSafetyRuleIDs: cv.BrandSafetyRuleIDs,
    UpdatedAt: cv.UpdatedAt,
    User: cv.User,
  } as ICreativeValidationRowData;
};

const areSetsEqual = (set1: Set<number | string>, set2: Set<number | string>): boolean => {
  // if one is undefined return false
  if (!set1 || !set2) {
    return false;
  }

  // Check if the sizes are the same
  if (set1.size !== set2.size) {
    return false;
  }

  // Check if every element in set1 is also in set2
  for (const item of set1) {
    if (!set2.has(item)) {
      return false;
    }
  }

  return true;
};

const getUpdateActions = (
  reviewStatus: number,
): {
  newStatus: number;
  text: string;
  iconName: string;
  color: string;
}[] => {
  const unreviewAction = createAction(
    REVIEW_STATUS_UNREVIEWED,
    'Unreview',
    'undo',
    publicaColors.black,
  );

  const allowAction = createAction(REVIEW_STATUS_ALLOWED, 'Allow', 'unlock', graphColors.green);

  const blockAction = createAction(
    REVIEW_STATUS_BLOCKED,
    'Block',
    'unavailable',
    publicaColors.red,
  );

  const reviewAction = createAction(
    REVIEW_STATUS_REVIEWED,
    'Review',
    'available',
    graphColors.lavenderPurple,
  );

  let actions;
  switch (reviewStatus) {
    case REVIEW_STATUS_ALLOWED:
      actions = [reviewAction, unreviewAction, blockAction];
      break;
    case REVIEW_STATUS_BLOCKED:
      actions = [reviewAction, unreviewAction, allowAction];
      break;
    case REVIEW_STATUS_BLOCKED_BY_API:
      actions = [reviewAction, unreviewAction, allowAction];
      break;
    case REVIEW_STATUS_REVIEWED:
      actions = [unreviewAction, allowAction, blockAction];
      break;
    default:
      // default return unreviewed update actions
      actions = [reviewAction, blockAction, allowAction];
      break;
  }
  return actions;
};

interface IBulkUpdateActionsProps {
  reviewStatus: number;
  multiSelected: boolean;
  selected: string[];
  handleBatchActionModalOpen: (newReviewStatus: number) => void;
  batchUpdateCreativeReview: (newReviewStatus: number) => void;
}

const BulkUpdateActions = ({
  reviewStatus,
  multiSelected,
  selected,
  handleBatchActionModalOpen,
  batchUpdateCreativeReview,
}: IBulkUpdateActionsProps): JSX.Element => {
  const actions = getUpdateActions(reviewStatus);

  return (
    <Cols
      wrap
      gap={spacing.large}
      style={{
        alignItems: 'center',
        rowGap: spacing.extraSmall,
      }}
    >
      <Col paddingRight={spacing.base}>
        <Text small color={muiColors.checkbox}>
          {`${selected.length} Creatives Selected`}
        </Text>
      </Col>
      {actions?.map((action) => (
        <Col
          onClick={() => {
            if (multiSelected) {
              handleBatchActionModalOpen(action.newStatus);
            } else {
              batchUpdateCreativeReview(action.newStatus);
            }
          }}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            paddingRight: spacing.small,
            gap: spacing.extraSmall,
          }}
        >
          <IconV2
            name={action.iconName}
            color={action.color}
            style={{ marginRight: spacing.extraSmall }}
            large
          />
          <Text bold color={action.color} style={{ alignItems: 'center' }}>
            {action.text}
          </Text>
        </Col>
      ))}
    </Cols>
  );
};

const convertConfigFiltersToState = (
  filters: ICustomCreativeReviewAPIFilterConfig,
): Record<string, Set<number | string>> => {
  const convertedFilters: Record<string, Set<number | string>> = {};

  for (const key in filters) {
    if (filters.hasOwnProperty(key)) {
      convertedFilters[key] = filters[key].selectedOptionIDs;
    }
  }
  return convertedFilters;
};

interface ICreativeReviewProps {
  classes: Record<string, string>;
}

export const CreativeReview = ({ classes }: ICreativeReviewProps): JSX.Element => {
  const { session, user } = useBaseStore();
  const readAuthorization = IS_AUTHORIZED(['read_channel_brandsafety_rule'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_channel_brandsafety_rule'], user.user);
  // get config
  const { BrandSafety } = useBaseConfig();
  const cfg = BrandSafety.CreativeReview.Main;
  const { cypressClassName, defaultState } = cfg;
  const isSuperUser = hasRoleSuperUser(user.user);

  cfg.isInitialized = true;

  // states and refs
  const isMounted = useRef(false);
  const [responseData, setResponseData] = useState<ICreativeValidationRowData[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [selectedFilterOptions, setSelectedFilterOptions] = useState<
    Record<string, Set<number | string>>
  >(convertConfigFiltersToState(cfg.filters)); // object of sets with all the filters
  // create a clone and save it with selected Filter options

  const reviewStatusFilter = Array.from(selectedFilterOptions.Status)[0] as number;

  const [listsLoadState, setListsLoadState] = useState(LoadingStateLoading);
  const [lists, setLists] = useState<Record<string, ISelectOption[]>>({});

  const [creativeRowData, setCreativeRowData] = useState<ICreativeValidationRowData>(
    {} as ICreativeValidationRowData,
  );
  const [updatingReviewStatus, setUpdatingReviewStatus] = useState<boolean>(false);
  const [openEditCreativeReviewModal, setOpenEditCreativeReviewModal] = useState(false);
  const [openBatchActionModal, setOpenBatchActionModal] = useState(false);
  const [batchUpdateStatus, setBatchUpdateStatus] = useState<number>(REVIEW_STATUS_UNREVIEWED);
  const [rules, setRules] = useState<IBrandSafetyRule[]>([]);

  // loading
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  // search
  const [query, setQuery] = useState('');

  // table states
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    (defaultState.activeColumnKeyMap && defaultState.activeColumnKeyMap[reviewStatusFilter]) ??
      extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const activeColumnsList = useMemo(
    () =>
      extractVisibleColumns(
        cfg.table.columns,
        (defaultState.activeColumnKeyMap && defaultState.activeColumnKeyMap[reviewStatusFilter]) ||
          [],
        isSuperUser,
      ),
    [reviewStatusFilter],
  );
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || '');
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);
  // bulk
  const [selected, setSelected] = useState<string[]>([]);

  const getFormattedDataFilters = (
    selectedFilterOptions: Record<string, Set<string | number>>,
  ): { [key: string]: string } => {
    const selectedFilters: { [key: string]: string } = {};
    Object.values(cfg.filters).forEach((filter) => {
      if (filter.apiKey && filter.convertForApi) {
        selectedFilters[filter.apiKey] = filter.convertForApi(filter.key, selectedFilterOptions);
      }
    });
    return selectedFilters;
  };

  // handles
  const getData = (newSelectedFilterOptions?: Record<string, Set<string | number>>): void => {
    const queryStart = moment().unix();
    track('Creative Review - Query initiated', { isSuperUser }, user.user);
    setLoadState(LoadingStateLoading);
    // convert filters selection to query properties

    const selectedFilters = getFormattedDataFilters(
      newSelectedFilterOptions || selectedFilterOptions,
    );
    getCreativeValidationsV2(session.publisher.ID, reviewStatusFilter, {
      order_by: orderBy,
      order,
      page,
      active_column_keys: activeColumnKeys,
      creative_url_filter: query,
      brand_safety_rule_ids: '',
      user_filter: '',
      ...selectedFilters,
    })
      .then((creativeValidations: ICreativeValidation[]) => {
        track('Creative Review - Data received', { isSuperUser }, user.user);
        // only show the first url
        creativeValidations = creativeValidations.map((validation) => {
          validation.CreativeURL = validation.CreativeURL.split('|||||')[0];
          return validation;
        });
        // resolve promise
        return creativeValidations;
      })
      .then((creativeValidations: ICreativeValidation[]) => {
        const cvsFromSnowflakeData = creativeValidations.map((value, idx) =>
          transformCreativeValidationToRowData(value, idx),
        );
        track(
          'Creative Review - Table displayed',
          { isSuperUser, queryTime: moment().unix() - queryStart },
          user.user,
        );
        // resolve the promise
        unstable_batchedUpdates(() => {
          setResponseData(cvsFromSnowflakeData);
          setLoadState(LoadingStateHasData);
          setTotalRowCount(creativeValidations.length > 0 ? creativeValidations[0].TotalCount : 0);
          setUpdatingReviewStatus(false);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setSnackbarMessage('Error retrieving data.');
          setSnackbarError(true);
          setLoadState(LoadingStateNoData);
          setTotalRowCount(0);
          setUpdatingReviewStatus(false);
        });
      });
  };

  // Populate lists with options
  const populateDropdowns = (): void => {
    setListsLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];
    const tempLists: Record<string, ISelectOption[]> = {};
    const tempSelectedFilterOptions = { ...selectedFilterOptions };
    Object.values(cfg.filters).forEach((dropdown) => {
      if (!dropdown.isChild) {
        keys.push(dropdown.key);
        getListFns.push(dropdown.getList(session, user));
        if (!isEmpty(dropdown.childKeys) && dropdown.childKeys) {
          dropdown.childKeys.forEach((childKey) => {
            keys.push(childKey);
            if (dropdown.selectedOptionIDs.values().next().value !== undefined) {
              getListFns.push(
                cfg.filters[childKey].getList(
                  session,
                  user,
                  dropdown.selectedOptionIDs.values().next().value as string,
                ),
              );
            } else {
              tempLists[childKey] = [];
            }
          });
        }
      } else {
        return;
      }
    });
    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, i) => {
          const filterKey = keys[i];
          tempLists[filterKey] = result;
          // Check if the values in selectedFilterOptions exist in tempLists
          if (tempSelectedFilterOptions[filterKey]) {
            const validOptions = new Set(tempLists[filterKey].map((option) => option.id));

            // Filter out values from selectedFilterOptions that don't exist in validOptions
            tempSelectedFilterOptions[filterKey] = new Set(
              Array.from(tempSelectedFilterOptions[filterKey]).filter((option) =>
                validOptions.has(option),
              ),
            );
          }
          Object.keys(tempSelectedFilterOptions).forEach((key) => {
            cfg.filters[key].selectedOptionIDs = tempSelectedFilterOptions[key];
          });
        });
        unstable_batchedUpdates(() => {
          setLists(tempLists);
          setListsLoadState(!isEmpty(tempLists) ? LoadingStateHasData : LoadingStateNoData);
          setSelectedFilterOptions(tempSelectedFilterOptions);
          getData(tempSelectedFilterOptions);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setLists({});
          setListsLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage('Error occurred while trying to load creative review filter data.');
        });
      });
  };

  const getRulesFromAPI = (): void => {
    // build request URL
    const reqURL = createUrl('/brandsafetyrules', session.publisher.ID);
    // make the request
    restGet<IBrandSafetyRule[]>(reqURL, {})
      .then((val: IHttpResponse<IBrandSafetyRule[]>) => {
        const apiRules = val.parsedBody as IBrandSafetyRule[];
        // OK
        setRules(apiRules);
        session.brandSafetyRules = apiRules;
      })
      .catch(() => {
        // error
        unstable_batchedUpdates(() => {
          setRules([]);
          setSnackbarError(true);
          setSnackbarMessage('Error while getting the list of rules from the API.');
        });
      });
  };

  // convert the data to use the new table interface
  const rows = formatCreativeReviewResponseIntoLVTRows(responseData, cfg.table);

  const batchUpdateCreativeReview = (newReviewStatus: number): void => {
    setLoadState(LoadingStateLoading);
    const creativeValidations = [] as Partial<ICreativeValidation>[];

    selected.forEach((id) => {
      const foundRow = rows[Number(id)];
      creativeValidations.push({
        OriginalCreativeURL: foundRow.OriginalCreativeURL.value as string,
        CreativeURL: foundRow.Name.value as string,
        Adomain: foundRow.Adomain.value as string,
        IABCategory: foundRow.IABCategoryCodes.value as string,
        ReviewStatus: foundRow.CustomLeftColumn.value as number,
        Notes: foundRow.Notes.value as string,
        AdomainOverride: foundRow.AdomainOverride.value as string,
        IABCategoryOverride: foundRow.IABCategoryOverride.value as string,
        BrandSafetyRuleIDs: foundRow.BrandSafetyRuleIDs.value as number[],
      } as Partial<ICreativeValidation>);
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    batchUpdateCreativeValidationStatus(
      session.publisher.ID,
      creativeValidations,
      newReviewStatus,
    ).then(([result, err]) => {
      if (err !== undefined) {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          console.error(err);
          setSnackbarMessage(err.toString());
          setSnackbarError(true);
          setOpenBatchActionModal(false);
        });
      }
      if (result !== undefined) {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setSnackbarMessage(
            selected.length === 1
              ? 'Successfully updated selected creative'
              : 'Successfully updated selected creatives',
          );
          setSnackbarError(false);
          setOpenBatchActionModal(false);
          setSelected([]);
          getData();
        });
      }
    });
  };

  const handleBatchActionModalOpen = (newReviewStatus: number): void => {
    track(
      'Creative Review - Bulk Action',
      { isSuperUser, action: cfg.reviewStatusMap[newReviewStatus].text },
      user.user,
    );
    setBatchUpdateStatus(newReviewStatus);
    setOpenBatchActionModal(true);
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    track('Creative Review - Column Header Sort', { isSuperUser, column: column.label }, user.user);
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setSelected([]);
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setSelected([]);
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleApplyFilterKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      setUpdatingReviewStatus(true);
    }
  };

  const handleSnackBar = (error: boolean, message: string): void => {
    unstable_batchedUpdates(() => {
      setSnackbarError(error);
      setSnackbarMessage(message);
    });
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    unstable_batchedUpdates(() => {
      setSelected([]);
      setPage(newPage);
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setSelected([]);
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  const handleSnackbarClose = (): void => {
    setSnackbarMessage('');
  };

  const handleOnChangeQuery = (text: string): void => {
    track('Creative Review - Search', { isSuperUser, search: text }, user.user);
    setQuery(text);
  };

  const handleEditCreativeReviewOpen = (rowData: ICreativeValidationRowData): void => {
    track('Creative Review - Edit creative', { isSuperUser }, user.user);
    setCreativeRowData(rowData);
    setOpenEditCreativeReviewModal(true);
  };

  // active columns
  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const areSameColumns = (keys: string[]): boolean => {
    return (
      keys.length === activeColumnKeys.length &&
      activeColumnKeys.every((activeColumnsKey) => keys.includes(activeColumnsKey))
    );
  };

  const handleAcceptActiveColumns = (keys: string[]): void => {
    track('Creative Review - Select Active Columns', { isSuperUser, columns: keys }, user.user);
    // setPage and column check is done here to prevent refetching data
    // since activeColumnKeys is used in the useEffect below as a listener to trigger a re-fetch
    setPage(0);
    if (!areSameColumns(keys)) {
      setActiveColumnKeys(keys);
    }
  };

  const genDownloadCreativeValidationCSV = (): void => {
    track('Creative Review - Download CSV', { isSuperUser }, user.user);
    const reviewStatus = cfg.reviewStatusMap[reviewStatusFilter].reviewStatus;
    const reqURL = addDomainAndTokenToUrl(
      `/publishers/${session.publisher.ID}/download_creative_validation_${reviewStatus}`,
    );

    const selectedFilters = getFormattedDataFilters(selectedFilterOptions);

    const reqUrlWithParams = addQueryParams(reqURL, {
      creative_url_filter: query,
      brand_safety_rule_ids: '',
      activeColumnKeys,
      ...selectedFilters,
    });

    window.open(reqUrlWithParams, '_blank', 'noreferrer');
  };

  const getBidderSettingsRoute = (id: string): string => {
    return getRoutePath(RouteBidderSettingsExisting, extractRouteParams({ session, user }), {
      b: id,
    });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      setSelected(responseData.map((val) => val.ID as string));
    } else {
      setSelected([]);
    }
  };

  const handleOnChangeSelectWithoutID = (id: string, _row: IListViewTableRow): void => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleClearFilters = (): void => {
    const filters = { ...cfg.filters };
    Object.values(filters).forEach((filter) => {
      if (filter.key !== 'Status') {
        filter.selectedOptionIDs = new Set([]);
        if (filter.childKeys && !isEmpty(filter.childKeys)) {
          filter.childKeys.forEach((childKey) => {
            filters[childKey].isDisabled = true;
          });
        }
      } else {
        filter.selectedOptionIDs = selectedFilterOptions.Status;
      }
    });
    const newSelectedFilterOptions = convertConfigFiltersToState(filters);
    setSelectedFilterOptions(newSelectedFilterOptions);
    getData(newSelectedFilterOptions);
  };

  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (areSetsEqual(value, selectedFilterOptions[key])) {
      return;
    }
    const tempSelectedFilterOptions = { ...selectedFilterOptions };
    const getListFns: Array<Promise<ISelectOption[]>> = [];
    const tempLists = { ...lists };
    const configFilterChildKeys = cfg.filters[key].childKeys;
    const childKeys: string[] = [];
    tempSelectedFilterOptions[key] = value;

    if (configFilterChildKeys) {
      configFilterChildKeys.forEach((childKey) => {
        tempSelectedFilterOptions[childKey] = new Set([]);
        const child = cfg.filters[childKey];
        if (isEmpty(value)) {
          child.isDisabled = true;
        } else {
          child.isDisabled = child.shouldDisable ? child.shouldDisable(value) : false;
          getListFns.push(child.getList(session, user, value.values().next().value as string));
          childKeys.push(childKey);
        }
      });

      Promise.all(getListFns)
        .then((results) => {
          // Iterate over results and populate child filter options
          results.forEach((result, index) => {
            tempLists[childKeys[index]] = result;
            cfg.filters[childKeys[index]].selectedOptionIDs = new Set([]);
          });

          // Update the UI with the new lists
          unstable_batchedUpdates(() => {
            setLists(tempLists);
            setListsLoadState(
              results.length > 0 && results.some((r) => r.length > 0)
                ? LoadingStateHasData
                : LoadingStateNoData,
            );
          });
        })
        .catch((err) => {
          console.error(err);
          unstable_batchedUpdates(() => {
            setLists({});
            setListsLoadState(LoadingStateNoData);
            setSnackbarError(true);
            setSnackbarMessage('Error occurred while trying to load data.');
          });
        });
    }
    setSelectedFilterOptions(tempSelectedFilterOptions);
  };

  const handleApplyFilters = (): void => {
    track(
      'Creative Review - Use filters',
      {
        isSuperUser,
        filters: Object.keys(selectedFilterOptions).filter(
          (key) => selectedFilterOptions[key].size > 0,
        ),
      },
      user.user,
    );
    Object.keys(selectedFilterOptions).forEach((key) => {
      cfg.filters[key].selectedOptionIDs = selectedFilterOptions[key];
    });
    getData();
  };

  // useEffects
  useEffect(() => {
    populateDropdowns();
    getRulesFromAPI();
    track('Creative Review - Open tab', { isSuperUser }, user.user);
  }, []);

  // recall api whenever these dependencies change
  useEffect(() => {
    if (isMounted.current) {
      getData();
    }
  }, [order, orderBy, page, rowsPerPage, query, reviewStatusFilter]);

  useEffect(() => {
    const keys =
      (defaultState.activeColumnKeyMap && defaultState.activeColumnKeyMap[reviewStatusFilter]) ??
      extractDefaultActiveColumnKeys(cfg.table.columns);
    // refer to handleAcceptActiveColumns for more details
    if (!areSameColumns(keys)) {
      setActiveColumnKeys(keys);
    }
  }, [reviewStatusFilter]);

  // update config on selected columns change
  useEffect(() => {
    if (isMounted.current) {
      if (!defaultState.activeColumnKeyMap) {
        defaultState.activeColumnKeyMap = {};
      }
      defaultState.activeColumnKeyMap[reviewStatusFilter] = activeColumnKeys;
      getData();
    }
  }, [activeColumnKeys]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, []);

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      {/* TOP BUTTONS */}
      <Panel>
        <Text color={publicaColors.darkGray} style={{ fontSize: fontSizing.small }}>
          Click a creatives' Adomain or IAB Category to view and edit its details. Select one or
          more creatives, then choose an action to apply: Review, Block, or Allow.
        </Text>
      </Panel>
      <Panel highlighted style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
        <CreativeReviewFilters
          cypressParentName={cypressClassName}
          filtersConfig={cfg.filters}
          selectedFilterOptions={selectedFilterOptions}
          handleFilterAccept={handleFilterAccept}
          handleClearFilters={handleClearFilters}
          handleApplyFilters={handleApplyFilters}
          options={lists}
          loadState={listsLoadState}
        />
      </Panel>
      <Panel highlighted largeHorizontalPadding verticalPadding>
        <Rows>
          <Row smallVerticalPadding noHorizontalPadding>
            <Cols style={{ justifyContent: 'space-between' }}>
              <Col>
                <Cols
                  wrap
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                    rowGap: spacing.small,
                  }}
                >
                  <Col
                    style={{
                      alignItems: 'center',
                      rowGap: spacing.small,
                      gap: spacing.medium,
                    }}
                  >
                    <TextInput
                      enableClear
                      handleOnChange={handleOnChangeQuery}
                      inputStyle={{ backgroundColor: publicaColors.veryLightGray }}
                      msDelay={700}
                      placeholder='Search by Creative URL'
                      prefix='search'
                      width='300px'
                    />
                  </Col>
                  <Col>
                    <SearchResultsText
                      numRowsDisplayed={responseData.length}
                      numTotalRows={totalRowCount}
                    />
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Cols
                  wrap
                  gap={spacing.extraSmall}
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    rowGap: spacing.extraSmall,
                  }}
                >
                  {/* CSV Button */}
                  <Col>
                    <IconTextToggle
                      buttonStyle={{ padding: '5px 12px 4px' }}
                      handleOnClick={genDownloadCreativeValidationCSV}
                      isActive={false}
                      label='CSV'
                      iconSlug='download'
                    />
                  </Col>
                  <Col>
                    <ColumnSelector
                      // simple multi select props
                      preselectedColumnKeys={
                        cfg.defaultState.activeColumnKeyMap &&
                        cfg.defaultState.activeColumnKeyMap[reviewStatusFilter]
                      }
                      resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                      columnsList={activeColumnsList}
                      columnsHandleOnChangeOption={handleChangeActiveColumns}
                      columnsHandleOnAccept={handleAcceptActiveColumns}
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Row>
          <Row style={{ padding: '4px 4px 8px', height: '30px' }}>
            {selected.length > 0 && (
              <BulkUpdateActions
                reviewStatus={reviewStatusFilter}
                selected={selected}
                multiSelected={selected.length > 1}
                handleBatchActionModalOpen={handleBatchActionModalOpen}
                batchUpdateCreativeReview={batchUpdateCreativeReview}
              />
            )}
          </Row>
          <Row noPadding>
            <ListViewTable
              activeColumnKeys={activeColumnKeys}
              columns={cfg.table.columns}
              customFunctions={{
                handleEditCreativeReviewOpen,
              }}
              customProps={{ user, isSuperUser }}
              displayNameWithID={false}
              enableActions
              enableSelectCheckbox
              enableCustomLeftColumn
              enableStatusSwitch={false}
              getItemSettingsRoute={getBidderSettingsRoute}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnChangeSelect={handleOnChangeSelectWithoutID}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              handleSelectAllClick={handleSelectAllClick}
              isActionPending={false}
              isReadAuthorized={readAuthorization.isAuthorized}
              isSuperUser={isSuperUser}
              isWriteAuthorized={writeAuthorization.isAuthorized}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              // TODO: update backend to accept rowsPerPage in order to use this feature
              rowsPerPageOptions={[{ value: 25, label: '25 Rows' }]}
              selected={selected}
              totalRowCount={totalRowCount}
              className={cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>
      <EditCreativeReviewModal
        classes={classes}
        openEditCreativeReviewModal={openEditCreativeReviewModal}
        handleClose={() => setOpenEditCreativeReviewModal(false)}
        creativeData={creativeRowData}
        handleApplyFilterKeyDown={handleApplyFilterKeyDown}
        getCreativeValidations={getData}
        handleCreativeReviewMessage={handleSnackBar}
        updatingReviewStatus={updatingReviewStatus}
        brandSafetyRules={rules}
        user={user.user}
        reviewStatusFilter={reviewStatusFilter}
      />
      <BatchUpdateModal
        open={openBatchActionModal}
        handleClose={() => setOpenBatchActionModal(false)}
        selectedNum={selected.length}
        newReviewStatus={batchUpdateStatus}
        handleUpdate={batchUpdateCreativeReview}
      />
      {/* snackbar is a special case and can act as a panel */}
      <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={handleSnackbarClose}>
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
